/**
 * Firebase INFRASTRUCTURE: the app, the auth instance, the tenant pin,
 * authFetch. Auth FLOWS (sign-in, MFA, session) live in modules/auth;
 * this file is the client identity plumbing every layer above may use.
 *
 * Dev/emulator: connectAuthEmulator points the SAME code path at the
 * compose stack's `firebase` service (AUTH emulator, port 9099). In
 * production builds the emulator URL is empty and the SDK talks to the
 * real project.
 */
import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { tracedFetch } from './tracing';

const AUTH_PROJECT_ID: string = import.meta.env.VITE_AUTH_PROJECT_ID ?? 'demo-polywog';
// Emulator by DEFAULT only in dev builds; production must declare a real
// project (and no emulator) at build time.
export const AUTH_EMULATOR_URL: string =
  import.meta.env.VITE_AUTH_EMULATOR_URL ?? (import.meta.env.DEV ? 'http://localhost:9099' : '');

export const firebaseApp = initializeApp({
  // With the emulator the apiKey is never validated; a real project's
  // web API key (NOT a secret -- it's a public client identifier) comes
  // in via VITE_AUTH_API_KEY at build time.
  apiKey: import.meta.env.VITE_AUTH_API_KEY ?? 'demo-api-key',
  projectId: AUTH_PROJECT_ID,
  authDomain: import.meta.env.VITE_AUTH_DOMAIN ?? `${AUTH_PROJECT_ID}.firebaseapp.com`,
});

export const auth = getAuth(firebaseApp);
if (AUTH_EMULATOR_URL) {
  connectAuthEmulator(auth, AUTH_EMULATOR_URL, { disableWarnings: true });
}

// Org-first tenancy (docs/ORG_TENANCY_SPEC.md): sign-in lands in the
// org's Identity Platform tenant. Until wildcard DNS resolves tenants
// by subdomain, the FIRST org's tenant id is pinned at build time
// (empty in emulator dev -- the emulator project has no tenants).
const AUTH_TENANT_ID: string = import.meta.env.VITE_AUTH_TENANT_ID ?? '';
if (AUTH_TENANT_ID) {
  auth.tenantId = AUTH_TENANT_ID;
}

/**
 * fetch with the current user's ID token attached -- the ONLY way the
 * app calls the api. The SDK caches the token and refreshes it before
 * expiry; getIdToken() is cheap after first mint. The request rides as
 * a span under the current interaction (src/api/tracing.ts), which is
 * what carries the traceparent: the browser is a process under the
 * process law of 2026-09-19, and the api links to it.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const user = auth.currentUser;
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init.headers).forEach((v, k) => headers.set(k, v));
  if (user) {
    headers.set('Authorization', `Bearer ${await user.getIdToken()}`);
  }
  return tracedFetch(input, { ...init, headers });
}
